import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore, Inbox as InboxIcon } from "@mui/icons-material";
import {
  useFeedbackInbox,
  useFeedbackMirrorRuns,
  useUpdateOwnerResponse,
  useUpdateTriage,
  TRIAGE_STATUS_LABELS,
  TRIAGE_STATUS_ORDER,
  type FeedbackTriageStatus,
  type FeedbackItem,
} from "@/hooks/useFeedback";

const TRIAGE_COLORS: Record<
  FeedbackTriageStatus,
  "default" | "primary" | "warning" | "success" | "error"
> = {
  new: "warning",
  triaged: "primary",
  in_progress: "primary",
  shipped: "success",
  declined: "default",
};

const CATEGORY_LABELS: Record<string, string> = {
  feature_request: "Feature request",
  bug_report: "Bug report",
};

function FeedbackCard({ item }: { item: FeedbackItem }) {
  const updateMutation = useUpdateTriage();
  const ownerMutation = useUpdateOwnerResponse();
  const [notes, setNotes] = useState(item.triage_notes ?? "");
  const [notesDirty, setNotesDirty] = useState(false);
  const [ownerResponse, setOwnerResponse] = useState(item.owner_response ?? "");
  const [ownerResponseDirty, setOwnerResponseDirty] = useState(false);

  const setStatus = (status: FeedbackTriageStatus) => {
    updateMutation.mutate({ id: item.id, triage_status: status });
  };
  const saveNotes = () => {
    updateMutation.mutate({ id: item.id, triage_notes: notes });
    setNotesDirty(false);
  };
  const saveOwnerResponse = () => {
    ownerMutation.mutate({ id: item.id, owner_response: ownerResponse });
    setOwnerResponseDirty(false);
  };

  return (
    <Accordion sx={{ "&:before": { display: "none" } }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Chip
            label={TRIAGE_STATUS_LABELS[item.triage_status]}
            color={TRIAGE_COLORS[item.triage_status]}
            size="small"
          />
          <Chip
            label={CATEGORY_LABELS[item.category] ?? item.category}
            size="small"
            variant="outlined"
          />
          <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {item.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(item.source_created_at).toLocaleDateString("en-GB")}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              From Evelina, prod KB · {item.source_article_id}
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "#fafafa",
                borderLeft: "3px solid",
                borderColor: "divider",
                whiteSpace: "pre-wrap",
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: "0.95rem",
                lineHeight: 1.6,
              }}
            >
              {item.content_markdown}
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Triage
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {TRIAGE_STATUS_ORDER.map((s) => (
                <Button
                  key={s}
                  size="small"
                  variant={item.triage_status === s ? "contained" : "outlined"}
                  color={TRIAGE_COLORS[s] === "default" ? "inherit" : TRIAGE_COLORS[s]}
                  disabled={updateMutation.isPending}
                  onClick={() => setStatus(s)}
                >
                  {TRIAGE_STATUS_LABELS[s]}
                </Button>
              ))}
            </Box>
          </Box>

          {item.implementation_plan && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Claude's proposed implementation plan
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#f3f8f3",
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                  whiteSpace: "pre-wrap",
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                }}
              >
                {item.implementation_plan}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}
              >
                Hint only — file paths may not be exact. Use as a starting point for Claude Code.
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Triage notes
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={notes}
              placeholder="Link to PR, decision, deferral reason…"
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesDirty(true);
              }}
            />
            {notesDirty && (
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={saveNotes}
                disabled={updateMutation.isPending}
              >
                Save notes
              </Button>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Your reply (Evelina — visible to Steve + Claude on next sync)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={ownerResponse}
              placeholder="Reply with answers to questions, extra detail, or 'this is no longer needed'…"
              onChange={(e) => {
                setOwnerResponse(e.target.value);
                setOwnerResponseDirty(true);
              }}
            />
            {ownerResponseDirty && (
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1 }}
                onClick={saveOwnerResponse}
                disabled={ownerMutation.isPending}
              >
                Save reply
              </Button>
            )}
            {item.owner_response_updated_at && !ownerResponseDirty && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Last reply: {new Date(item.owner_response_updated_at).toLocaleString("en-GB")}
              </Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Last mirrored {new Date(item.last_mirrored_at).toLocaleString("en-GB")} · prod updated{" "}
            {new Date(item.source_updated_at).toLocaleString("en-GB")}
            {item.triage_updated_at &&
              ` · triage updated ${new Date(item.triage_updated_at).toLocaleString("en-GB")}`}
          </Typography>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function MirrorRunsHint() {
  const { data: runs } = useFeedbackMirrorRuns();
  if (!runs || runs.length === 0) return null;
  const latest = runs[0];
  return (
    <Typography variant="caption" color="text.secondary">
      Last mirror sync: {new Date(latest.started_at).toLocaleString("en-GB")} ·{" "}
      {latest.error_message
        ? `failed (${latest.error_message.slice(0, 80)}…)`
        : `${latest.rows_seen ?? 0} rows seen`}
    </Typography>
  );
}

export default function FeedbackInbox() {
  const [statusFilter, setStatusFilter] = useState<FeedbackTriageStatus | "">("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  const { data, isLoading, error } = useFeedbackInbox({
    triage_status: statusFilter || undefined,
    category: categoryFilter || undefined,
  });

  const grouped = useMemo(() => {
    const groups = new Map<FeedbackTriageStatus, FeedbackItem[]>();
    for (const item of data?.items ?? []) {
      const arr = groups.get(item.triage_status) ?? [];
      arr.push(item);
      groups.set(item.triage_status, arr);
    }
    return groups;
  }, [data]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <InboxIcon color="action" />
        <Typography variant="h4" sx={{ flex: 1 }}>
          Feedback Inbox
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mirrors prod KB articles in the <code>feature_request</code> and <code>bug_report</code>{" "}
        categories. Triage status + notes live only on this side and let Claude / Steve track what's
        been read, planned, shipped, or deferred without polluting Evelina's KB.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FeedbackTriageStatus | "")}
          >
            <MenuItem value="">All statuses</MenuItem>
            {TRIAGE_STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>
                {TRIAGE_STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All categories</MenuItem>
            <MenuItem value="feature_request">Feature requests</MenuItem>
            <MenuItem value="bug_report">Bug reports</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <MirrorRunsHint />
      </Box>

      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">Failed to load feedback inbox.</Alert>}

      {data && data.items.length === 0 && (
        <Alert severity="info">
          Nothing matches the current filter. Either the daily mirror hasn't synced yet, or
          everything has been triaged into another bucket.
        </Alert>
      )}

      {data && data.items.length > 0 && (
        <Box>
          {TRIAGE_STATUS_ORDER.map((status) => {
            const items = grouped.get(status) ?? [];
            if (items.length === 0) return null;
            return (
              <Box key={status} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    {TRIAGE_STATUS_LABELS[status]} ({items.length})
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Divider />
                  </Box>
                </Box>
                <Stack spacing={0.5}>
                  {items.map((item) => (
                    <FeedbackCard key={item.id} item={item} />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
